import React, { useState, useEffect, useRef } from 'react';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';
import { useAudioAnalyzer } from '../hooks/useAudioAnalyzer';
import { ideaApi } from '../services/api';
import toast from 'react-hot-toast';
import './EnhancedVoiceRecorder.css';

interface EnhancedVoiceRecorderProps {
  onIdeaCreated: () => void;
}

export const EnhancedVoiceRecorder: React.FC<EnhancedVoiceRecorderProps> = ({ onIdeaCreated }) => {
  const { isRecording, audioBlob, startRecording, stopRecording, resetRecording } = useVoiceRecorder();
  const { metrics, startAnalyzing, stopAnalyzing } = useAudioAnalyzer();
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [autoStopTimer, setAutoStopTimer] = useState<NodeJS.Timeout | null>(null);
  const silenceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Update recording duration
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } else {
      setRecordingDuration(0);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  // Auto-stop on extended silence
  useEffect(() => {
    if (isRecording && metrics.isSilent) {
      if (!silenceTimerRef.current) {
        silenceTimerRef.current = setTimeout(() => {
          handleStopRecording();
          toast.success('Recording stopped due to silence');
        }, 3000); // Stop after 3 seconds of silence
      }
    } else {
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
    }
  }, [isRecording, metrics.isSilent]);

  const handleStartRecording = async () => {
    try {
      // Request microphone permission and get stream
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      streamRef.current = stream;
      
      // Start recording
      await startRecording();
      
      // Start audio analysis
      startAnalyzing(stream);
      
      toast.success('Recording started - speak clearly!');
    } catch (error) {
      toast.error('Failed to start recording. Please check microphone permissions.');
    }
  };

  const handleStopRecording = () => {
    stopRecording();
    stopAnalyzing();
    
    // Stop all audio tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    // Clear any timers
    if (autoStopTimer) {
      clearTimeout(autoStopTimer);
      setAutoStopTimer(null);
    }
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    
    toast.success('Recording stopped');
  };

  const handleSubmit = async () => {
    if (!audioBlob) return;

    setIsProcessing(true);
    try {
      await ideaApi.createVoiceNote(audioBlob);
      toast.success('Voice note uploaded! Transcribing...');
      resetRecording();
      onIdeaCreated();
    } catch (error) {
      toast.error('Failed to upload voice note');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getQualityIndicator = () => {
    if (metrics.isClipping) return { text: 'Too loud!', color: 'red' };
    if (metrics.noiseLevel > 50) return { text: 'Noisy', color: 'orange' };
    if (metrics.volume < 10 && !metrics.isSilent) return { text: 'Too quiet', color: 'orange' };
    if (metrics.isSilent) return { text: 'Silent', color: 'gray' };
    return { text: 'Good', color: 'green' };
  };

  return (
    <div className="enhanced-voice-recorder">
      <div className="recorder-main">
        {!isRecording && !audioBlob && (
          <button 
            onClick={handleStartRecording}
            className="record-button large"
            aria-label="Start recording"
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
              <line x1="8" y1="23" x2="16" y2="23" />
            </svg>
            <span>Start Recording</span>
          </button>
        )}

        {isRecording && (
          <div className="recording-interface">
            <div className="volume-meter">
              <div 
                className="volume-bar"
                style={{ 
                  width: `${metrics.volume}%`,
                  backgroundColor: metrics.isClipping ? '#ef4444' : '#10b981'
                }}
              />
            </div>
            
            <div className="recording-info">
              <div className="recording-indicator">
                <span className="recording-dot"></span>
                Recording
              </div>
              <div className="duration">{formatDuration(recordingDuration)}</div>
              <div className={`quality-indicator ${getQualityIndicator().color}`}>
                {getQualityIndicator().text}
              </div>
            </div>

            <button 
              onClick={handleStopRecording}
              className="stop-button"
              aria-label="Stop recording"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="6" width="12" height="12" />
              </svg>
              Stop Recording
            </button>

            {metrics.noiseLevel > 30 && (
              <div className="recording-tip">
                💡 Tip: Find a quieter environment for better transcription
              </div>
            )}
          </div>
        )}

        {audioBlob && !isRecording && (
          <div className="audio-preview">
            <audio controls src={URL.createObjectURL(audioBlob)} />
            <div className="preview-info">
              <span>Duration: {formatDuration(recordingDuration)}</span>
              <span>Ready to upload</span>
            </div>
            <div className="preview-actions">
              <button 
                onClick={handleSubmit} 
                disabled={isProcessing}
                className="submit-button primary"
              >
                {isProcessing ? 'Uploading...' : 'Save Voice Note'}
              </button>
              <button 
                onClick={resetRecording}
                disabled={isProcessing}
                className="cancel-button"
              >
                Discard
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="recorder-tips">
        <h4>Voice Recording Tips</h4>
        <ul>
          <li>Speak clearly and at a normal pace</li>
          <li>Recording auto-stops after 3 seconds of silence</li>
          <li>Find a quiet environment for best results</li>
          <li>Keep your device 6-12 inches away</li>
        </ul>
      </div>
    </div>
  );
};